import { useMutation } from '@tanstack/react-query';
import processSystemFile from '../../../../utils/processSystemFile';
import urls from '../../../../utils/urls';
import { APITypes } from '../../../../primary-record-types';
import useNotify from '../../../../hooks/useNotify';
import { getIntl } from '../../../../utils/intl';
import { PickedFile } from '..';
import { appAPI } from '../../../../utils/api';
import { notifyBugsnag } from '../../../../utils/bugsnag';

const intl = getIntl('useTempFile', {
    uploadError: 'Error uploading file',
});

export type TempFileValue = {
    base64: string;
    tempFileId: string;
    name: string;
    originalName?: string;
    contentType: string;
};

export type UseTempFileReturnValue = {
    onFileSelected: (pickedFile: PickedFile) => void;
    isUploading: boolean;
};

export type UseTempFileArgs = {
    value: TempFileValue | null;
    onChange: (value: TempFileValue | null) => void;
};

export const useUploadTempFile = () => {
    const notify = useNotify();
    return async (pickedFile: PickedFile): Promise<TempFileValue | null> => {
        const {
            uri,
            name,
            contentType,
            s3UploadBuffer: buffer,
            originalName,
        } = await processSystemFile({ pickedImage: pickedFile });

        const response = (await appAPI.post(urls.api.files.tempFiles, {
            body: {
                name,
                contentType,
            } as APITypes.fileService.CreateTempFileBody,
        })) as APITypes.fileService.CreateTempFileResponse;

        try {
            const { tempFileId, uploadUrl } = response;

            const uploadResponse = await fetch(uploadUrl, {
                method: 'PUT',
                body: buffer as BodyInit,
                headers: {
                    'Content-Type': contentType,
                    'Content-Encoding': 'base64',
                },
            });

            if (uploadResponse.ok) {
                return { base64: uri, tempFileId, name, originalName, contentType };
            } else {
                notify('error', intl('uploadError'));
                console.error('Error uploading file:', uploadResponse.status, uploadResponse);
            }
        } catch (e) {
            console.error('Error uploading file:', e);
            notify('error', intl('uploadError'));
            notifyBugsnag(e as any);
        }

        return null;
    };
};

export default function useTempFile({ onChange }: UseTempFileArgs): UseTempFileReturnValue {
    const uploadFile = useUploadTempFile();
    const uploadFileMutation = useMutation({
        onMutate: async (pickedFile: PickedFile) => {
            const uploadedFile = await uploadFile(pickedFile);
            if (uploadedFile) {
                onChange(uploadedFile);
            }
        },
    });

    return {
        onFileSelected: uploadFileMutation.mutate,
        isUploading: uploadFileMutation.isPending,
    };
}

export type UseTempFilesReturnValue = {
    onFilesSelected: (pickedFiles: PickedFile[]) => void;
    isUploading: boolean;
};

export type UseTempFilesArgs = {
    value: TempFileValue[] | null;
    onChange: (value: TempFileValue[] | null) => void;
};

export function useTempFiles({ onChange }: UseTempFilesArgs) {
    const uploadFile = useUploadTempFile();
    const uploadFilesMutation = useMutation({
        onMutate: async (pickedFiles: PickedFile[]) => {
            const uploadedFiles = await Promise.all(pickedFiles.map(uploadFile));
            onChange(uploadedFiles.filter((file) => file) as TempFileValue[]);
        },
    });

    return {
        onFileSelected: uploadFilesMutation.mutate,
        isUploading: uploadFilesMutation.isPending,
    };
}
